import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Select, DatePicker, Card, message, Spin, Typography, Button, Modal, List, Space, Switch, Tooltip } from 'antd';
import { HotTable } from '@handsontable/react';
import { registerAllModules } from 'handsontable/registry';
import Handsontable from 'handsontable';
import { ReloadOutlined, SettingOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import 'handsontable/dist/handsontable.full.css';
import '../../styles/handsontable.css';
import dayjs from 'dayjs';
import api, { monthlyMetricService } from '../../services/api';
import MonthlyMetricsTable from './MonthlyMetricsTable';
import 'dayjs/locale/tr';

const { Title } = Typography;

dayjs.locale('tr');
registerAllModules();

// Rengin açık mı koyu mu olduğunu kontrol eden yardımcı fonksiyon
const isColorLight = (color) => {
  // Hex renk kodunu RGB'ye dönüştür
  let r, g, b;
  if (color.startsWith('#')) {
    const hex = color.substring(1);
    r = parseInt(hex.substr(0, 2), 16);
    g = parseInt(hex.substr(2, 2), 16);
    b = parseInt(hex.substr(4, 2), 16);
  } else if (color.startsWith('rgb')) {
    const rgbValues = color.match(/\d+/g);
    if (rgbValues && rgbValues.length >= 3) {
      r = parseInt(rgbValues[0]);
      g = parseInt(rgbValues[1]);
      b = parseInt(rgbValues[2]);
    } else {
      return true; // Varsayılan olarak açık renk kabul et
    }
  } else {
    return true; // Bilinmeyen format için varsayılan olarak açık renk kabul et
  }

  // Rengin parlaklığını hesapla (YIQ formülü)
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return yiq >= 128; // 128'den büyükse açık renk, değilse koyu renk
};

// Rengi açıklaştıran yardımcı fonksiyon
const lightenColor = (color, factor) => {
  // Hex renk kodunu RGB'ye dönüştür
  let r, g, b;
  if (color.startsWith('#')) {
    const hex = color.substring(1);
    r = parseInt(hex.substr(0, 2), 16);
    g = parseInt(hex.substr(2, 2), 16);
    b = parseInt(hex.substr(4, 2), 16);
  } else if (color.startsWith('rgb')) {
    const rgbValues = color.match(/\d+/g);
    if (rgbValues && rgbValues.length >= 3) {
      r = parseInt(rgbValues[0]);
      g = parseInt(rgbValues[1]);
      b = parseInt(rgbValues[2]);
    } else {
      return color; // Geçersiz format için orijinal rengi döndür
    }
  } else {
    return color; // Bilinmeyen format için orijinal rengi döndür
  }

  // Rengi açıklaştır
  r = Math.min(255, Math.round(r + (255 - r) * factor));
  g = Math.min(255, Math.round(g + (255 - g) * factor));
  b = Math.min(255, Math.round(b + (255 - b) * factor));

  // RGB'yi Hex'e dönüştür
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

const DataEntry = () => {
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().startOf('month'));
  const [loading, setLoading] = useState(false);
  const [metrics, setMetrics] = useState([]);
  const [allMetrics, setAllMetrics] = useState([]);
  const [metricVisibility, setMetricVisibility] = useState({});
  const [visibilityModalVisible, setVisibilityModalVisible] = useState(false);
  const [gridData, setGridData] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [pendingChanges, setPendingChanges] = useState(new Map());
  const [dataForGrid, setDataForGrid] = useState([]);

  // MonthlyMetricsTable referansı için
  const monthlyMetricsTableRef = useRef(null);
  // HotTable referansı için
  const hotTableRef = useRef(null);

  const columns = useMemo(() => {
    const sortedMetrics = [...metrics].sort((a, b) => {
      const orderA = a.BrandMetric?.displayOrder || 0;
      const orderB = b.BrandMetric?.displayOrder || 0;
      return orderA - orderB;
    });

    const result = sortedMetrics.map(metric => ({
      data: `metric_${metric.id}`,
      type: metric.dataType === 'text' ? 'text' : 'numeric',
      title: metric.description || metric.name,
      readOnly: metric.isAutoCalculated,
      numericFormat: {
        pattern: {
          'number': '0,0.00',
          'currency': '₺0,0.00',
          'percentage': '%0,0.00'
        }[metric.dataType] || '0,0.00',
        culture: 'tr-TR'
      }
    }));

    console.log('Oluşturulan sütunlar:', result.map(col => ({
      title: col.title,
      data: col.data,
      metric: metrics.find(m => `metric_${m.id}` === col.data),
      displayOrder: metrics.find(m => `metric_${m.id}` === col.data)?.BrandMetric?.displayOrder
    })));

    return result;
  }, [metrics]);

  const fetchBrands = async () => {
    try {
      setInitialLoading(true);
      const response = await api.get('/brands');
      
      // Markaları displayOrder'a göre sırala
      const sortedBrands = response.data.sort((a, b) => {
        const orderA = a.displayOrder || 0;
        const orderB = b.displayOrder || 0;
        return orderA - orderB;
      });
      
      setBrands(sortedBrands);
      
      if (sortedBrands.length > 0) {
        setSelectedBrand(sortedBrands[0].id);
      }
    } catch (error) {
      message.error('Markalar yüklenirken bir hata oluştu');
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchBrandMetrics = useCallback(async () => {
    if (!selectedBrand) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/brands/${selectedBrand}/metrics`);
      
      // Tüm metrikleri al
      const activeMetrics = response.data
        .filter(metric => metric.BrandMetric?.isActive)
        .sort((a, b) => {
          const orderA = a.BrandMetric?.displayOrder || 0;
          const orderB = b.BrandMetric?.displayOrder || 0;
          return orderA - orderB;
        });
      
      // Sadece görünür olanları filtrele
      const visibleMetrics = activeMetrics
        .filter(metric => metric.BrandMetric?.isVisible !== false);

      console.log('API\'den gelen ham veri:', response.data);
      console.log('Tüm aktif metrikler:', activeMetrics.map(m => ({
        id: m.id,
        name: m.name,
        displayOrder: m.BrandMetric?.displayOrder,
        isVisible: m.BrandMetric?.isVisible,
        rawBrandMetric: m.BrandMetric
      })));
      
      // Metrik görünürlük state'ini oluştur
      const newMetricVisibility = {};
      activeMetrics.forEach(metric => {
        newMetricVisibility[metric.id] = metric.BrandMetric?.isVisible !== false;
      });

      setAllMetrics(activeMetrics);
      setMetrics(visibleMetrics);
      setMetricVisibility(newMetricVisibility);
    } catch (error) {
      console.error('Metrik yükleme hatası:', error);
      message.error('Metrikler yüklenirken bir hata oluştu');
      setMetrics([]);
      setAllMetrics([]);
    } finally {
      setLoading(false);
    }
  }, [selectedBrand]);

  const fetchMonthData = useCallback(async () => {
    if (!selectedBrand || !selectedMonth) return;
    
    try {
      setLoading(true);
      const startDate = dayjs(selectedMonth).startOf('month');
      const endDate = dayjs(selectedMonth).endOf('month');
      
      const response = await api.get('/data', {
        params: {
          brandId: selectedBrand,
          startDate: startDate.format('YYYY-MM-DD'),
          endDate: endDate.format('YYYY-MM-DD')
        }
      });

      // Detaylı loglama
      console.log('API Yanıtı (Detaylı):', JSON.stringify(response.data, null, 2));
      
      // API yanıtını saklayalım - API verisi kontrolü için
      setDataForGrid(response.data);

      // Aylık metrik değerlerini al
      let monthlyMetricValues = [];
      if (monthlyMetricsTableRef.current?.gridData?.originalData) {
        // Sadece görünür metrikleri filtrele - artık bu filtremeye gerek yok
        // çünkü aylık metrikler kendi görünürlük kontrol mekanizmasını kullanıyor
        monthlyMetricValues = monthlyMetricsTableRef.current.gridData.originalData;
      }

      // Ayın tüm günlerini oluştur
      const daysInMonth = selectedMonth.daysInMonth();
      const newGridData = [];
      
      // İlişkili aylık metrik ID'lerini saklamak için geçici bir nesne
      const linkedMonthlyMetricIds = {};
      
      // Önce günlük değerleri ekle
      for (let day = 1; day <= daysInMonth; day++) {
        const currentDate = dayjs(selectedMonth).date(day);
        const rowData = {
          date: currentDate.format('DD.MM.YYYY'),
          dateObj: currentDate,
          isMonthlyRow: false
        };

        // Her metrik için o güne ait değeri bul
        metrics.forEach(metric => {
          const metricId = metric.id;
          
          // API yanıtındaki veri yapısına göre uygun veriyi bul
          const dataForDay = response.data.find(
            item => 
              // Metrik ID kontrolü - API yanıtına göre uyarlandı
              (item.Metric?.id === metricId || item.metricId === metricId) && 
              // Tarih kontrolü - gün bazında eşleşme
              dayjs(item.date).date() === day
          );
          
          // İlişkili aylık metrik ID'sini sakla (API'den geliyorsa)
          if (dataForDay && dataForDay.linkedMonthlyMetricId) {
            linkedMonthlyMetricIds[metricId] = dataForDay.linkedMonthlyMetricId;
          }
          
          rowData[`metric_${metricId}`] = dataForDay ? dataForDay.value : null;
        });
        
        newGridData.push(rowData);
      }
      
      // En sona aylık metrik değerlerini içeren satırı ekle
      const monthlyRow = {
        date: `${selectedMonth.format('MMMM YYYY')} `,
        dateObj: null,
        isMonthlyRow: true
      };

      // Her metrik için ilişkili aylık metrik değerini bul
      metrics.forEach(metric => {
        // Önce BrandMetric'ten, yoksa geçici nesneden al
        const linkedMonthlyMetricId = metric.BrandMetric?.linkedMonthlyMetricId || linkedMonthlyMetricIds[metric.id];
        
        if (linkedMonthlyMetricId) {
          const monthlyMetric = monthlyMetricValues.find(m => m.id === linkedMonthlyMetricId);
          if (monthlyMetric) {
            monthlyRow[`metric_${metric.id}`] = monthlyMetric.value;
          } else {
            monthlyRow[`metric_${metric.id}`] = null;
          }
        } else {
          monthlyRow[`metric_${metric.id}`] = null;
        }
      });

      // Aylık değerler satırını en sona ekle
      newGridData.push(monthlyRow);
      
      // Detaylı loglama
      console.log('HotTable\'a gönderilecek yeni veri:', JSON.stringify(newGridData, null, 2));
      setGridData(newGridData);
      
      // Tabloyu manuel olarak güncellemeye zorla
      setTimeout(() => {
        if (hotTableRef.current?.hotInstance) {
          console.log('fetchMonthData sonrası HotTable güncellemesi zorlanıyor...');
          // loadData, veriyi doğrudan tabloya yükler
          hotTableRef.current.hotInstance.loadData(newGridData); 
          // Gerekirse render'ı da tetikle
          hotTableRef.current.hotInstance.render(); 
        }
      }, 50); // Çok kısa bir gecikme
      
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
      message.error('Veriler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand, selectedMonth, metrics, columns]);

  // saveData fonksiyonu kullanılmıyor, ancak ileride kullanılabilir
  // const saveData = async (entry) => {
  //   if (!selectedBrand) return;
  //   
  //   try {
  //       setLoading(true);
  //       console.log('Veri kaydediliyor:', entry);
  //       
  //       // Mevcut veriyi kontrol et
  //       const response = await api.get('/data', {
  //           params: {
  //               brandId: selectedBrand.id,
  //               date: entry.date
  //           }
  //       });
  //       
  //       // ... existing code ...
  //   } catch (error) {
  //       // ... existing code ...
  //   } finally {
  //       // ... existing code ...
  //   }
  // };

  const handleGridChange = async (changes) => {
    if (!changes || changes.length === 0) return;
    
    // Güvenlik kontrolü: 4 günden eski veri girişi veya silme işlemi için onay iste
    const fourDaysAgo = dayjs().subtract(4, 'day').startOf('day');
    let hasOldDateEntry = false;
    let hasDataDeletion = false;
    let oldestDate = null;
    
    for (const [row, prop, oldValue, newValue] of changes) {
      if (prop.startsWith('metric_')) {
        const rowData = gridData[row];
        if (rowData && rowData.dateObj && !rowData.isMonthlyRow) {
          const entryDate = rowData.dateObj;
          
          // 4 günden eski tarih kontrolü (sadece yeni veri girişinde)
          if (entryDate.isBefore(fourDaysAgo) && oldValue !== newValue && newValue !== null && newValue !== '') {
            hasOldDateEntry = true;
            if (!oldestDate || entryDate.isBefore(oldestDate)) {
              oldestDate = entryDate;
            }
          }
          
          // Veri silme kontrolü (değer var iken boş/null yapılması)
          if ((oldValue !== null && oldValue !== '' && oldValue !== undefined) && 
              (newValue === null || newValue === '' || newValue === undefined)) {
            hasDataDeletion = true;
          }
        }
      }
    }
    
    // Onay modalları göster
    if (hasOldDateEntry) {
      const confirmed = await new Promise(resolve => {
        Modal.confirm({
          title: 'Eski Tarihli Veri Girişi',
          content: `${oldestDate.format('DD MMMM YYYY')} tarihli veri girişi yapıyorsunuz. Devam etmek istediğinizden emin misiniz?`,
          okText: 'Evet, Devam Et',
          cancelText: 'İptal Et',
          onOk: () => resolve(true),
          onCancel: () => resolve(false)
        });
      });
      
      if (!confirmed) {
        // Kullanıcı iptal ettiyse değişiklikleri geri al
        message.info('Veri girişi iptal edildi');
        await fetchMonthData(); // Tabloyu yenile
        return;
      }
    }
    
    if (hasDataDeletion) {
      const confirmed = await new Promise(resolve => {
        Modal.confirm({
          title: 'Veri Silme İşlemi',
          content: 'Mevcut verileri silmek üzeresiniz. Bu işlem geri alınamaz. Devam etmek istediğinizden emin misiniz?',
          okText: 'Evet, Sil',
          cancelText: 'İptal Et',
          okType: 'danger',
          onOk: () => resolve(true),
          onCancel: () => resolve(false)
        });
      });
      
      if (!confirmed) {
        // Kullanıcı iptal ettiyse değişiklikleri geri al
        message.info('Veri silme işlemi iptal edildi');
        await fetchMonthData(); // Tabloyu yenile
        return;
      }
    }
    
    // Değişiklikleri topla
    const batchChanges = [];
    const updatedCells = [];
    
    for (const [row, prop, oldValue, newValue] of changes) {
      if (prop.startsWith('metric_')) {
        const metricId = parseInt(prop.replace('metric_', ''));
        const metric = metrics.find(m => m.id === metricId);
        
        if (!metric || metric.isAutoCalculated) {
          message.warning('Bu değer düzenlenemez');
          continue;
        }

        let processedValue = newValue;

        // Boş değer kontrolü
        if (newValue === null || newValue === '') {
          processedValue = null;
        } else if (metric.dataType !== 'text') {
          // Sayısal değer kontrolü
          const cleanValue = newValue.toString().replace(/[^0-9.-]/g, '');
          const numValue = Number(cleanValue);
          
          if (isNaN(numValue)) {
            message.warning('Lütfen geçerli bir sayı girin');
            continue;
          }
          processedValue = numValue;
        }

        // Veri kaynağı bilgisini kontrol et (API'den gelmiş mi?)
        const currentDate = gridData[row]?.dateObj?.format('YYYY-MM-DD');
        let isApiData = false;
        
        if (currentDate) {
          const apiData = dataForGrid.find(item => 
            (item.Metric?.id === metric.id || item.metricId === metric.id) && 
            dayjs(item.date).format('YYYY-MM-DD') === currentDate
          );
          
          // Eğer veri kaynağı 'api' ise işaretle
          if (apiData && apiData.source === 'api') {
            isApiData = true;
          }
        }

        console.log('Veri girişi:', {
          metric: metric.name,
          value: processedValue,
          date: gridData[row].dateObj.format('YYYY-MM-DD'),
          isApiData
        });

        // Değişikliği batch listesine ekle
        batchChanges.push({
          metricId: metricId,
          date: gridData[row].dateObj.format('YYYY-MM-DD'),
          value: processedValue,
          dateObj: gridData[row].dateObj,
          isApiData // API'den gelmiş veriyi işaretle
        });
        
        // Güncellenen hücreleri takip et
        updatedCells.push({
          row,
          prop,
          value: processedValue
        });
      }
    }
    
    // Eğer değişiklik yoksa işlem yapma
    if (batchChanges.length === 0) return;
    
    // Önce UI'da değişiklikleri göster
    const newGridData = [...gridData];
    updatedCells.forEach(cell => {
      if (newGridData[cell.row]) {
        newGridData[cell.row][cell.prop] = cell.value;
      }
    });
    setGridData(newGridData);
    
    // Yükleme göstergesini sadece 300ms'den uzun süren işlemler için göster
    let loadingTimeout = setTimeout(() => {
      setLoading(true);
    }, 300);
    
    try {
      // Değişiklikleri grupla (aynı gün için)
      const groupedChanges = batchChanges.reduce((acc, change) => {
        const dateKey = change.date;
        if (!acc[dateKey]) {
          acc[dateKey] = {
            brandId: selectedBrand,
            date: dateKey,
            entries: []
          };
        }
        acc[dateKey].entries.push({
          metricId: change.metricId,
          value: change.value?.toString() || null,
          isApiData: change.isApiData // API'den gelmiş veriyi işaretle
        });
        return acc;
      }, {});
      
      // Her gün için tek bir API çağrısı yap
      await Promise.all(
        Object.values(groupedChanges).map(async (dayData) => {
          const response = await api.post('/data', {
            ...dayData,
            getApi: false,  // Frontend'den gelen istekleri işaretlemek için
            updateSource: true // API'den alınmış kayıtların kaynağını frontend olarak değiştir
          });
          return response.data;
        })
      );
      
      message.success('Veriler başarıyla kaydedildi');
      
      // Tüm verileri sessizce yenile (loading göstermeden)
      clearTimeout(loadingTimeout);
      setLoading(false);
      
      // Günlük metrikler değiştirildiğinde verileri yenile (BU ÇAĞRI KALDIRILDI - Recalculate sonrası yapılacak)
      // fetchMonthData().catch(error => {
      //   console.error('Veri yenileme hatası:', error);
      // });
      
      // Aylık metrikleri otomatik olarak yeniden hesapla
      try {
        console.log('Günlük değişiklik sonrası aylık metrikler hesaplanıyor...');
        const result = await monthlyMetricService.recalculateMonthlyMetrics(
          selectedBrand,
          selectedMonth.format('YYYY-MM')
        );

        if (result.success) {
          console.log('Aylık metrikler otomatik olarak güncellendi (günlük değişiklik sonrası)');
        } else {
          console.error('Aylık metrikler otomatik olarak güncellenemedi (günlük değişiklik sonrası):', result.error);
          // Hata olsa bile devam edip veriyi çekmeyi deneyebiliriz.
        }
      } catch (error) {
        console.error('Aylık metrik güncelleme hatası (günlük değişiklik sonrası):', error);
        // Hata olsa bile devam edip veriyi çekmeyi deneyebiliriz.
      } finally {
        // *** EN ÖNEMLİ DEĞİŞİKLİK: Tüm işlemlerden SONRA veriyi çek ***
        console.log('handleGridChange: Tüm işlemler bitti, fetchMonthData çağrılıyor.');
        try {
          await fetchMonthData();
        } catch (fetchError) {
           console.error('Son fetchMonthData hatası:', fetchError);
           message.error('Veriler yenilenirken bir hata oluştu.');
        }
      }
      
    } catch (error) {
      console.error('Veri kaydetme hatası:', error);
      message.error('Veriler kaydedilirken bir hata oluştu');
      clearTimeout(loadingTimeout);
      // Hata durumunda da yenileme yapalım
      try {
        await fetchMonthData();
      } catch (fetchError) {
         console.error('Dış catch fetchMonthData hatası:', fetchError);
         message.error('Hata sonrası veriler yenilenirken bir hata oluştu.');
      } finally {
          // loading state'ini burada yönetmek daha güvenli olabilir
          setLoading(false);
      }
    }
  };

  const handleMonthChange = (date) => {
    if (!date) return;
    setSelectedMonth(dayjs(date).startOf('month'));
    setIsPickerOpen(false);
  };

  const disabledDate = (current) => {
    if (!current) return false;

    // Geçmiş 5 yıl ve gelecek 1 yıl dışındaki tarihleri devre dışı bırak
    const start = dayjs().subtract(5, 'year').startOf('month');
    const end = dayjs().add(1, 'year').endOf('month');
    
    return current && (current.isBefore(start) || current.isAfter(end));
  };

  // Aylık metrikleri yeniden hesaplama fonksiyonu
  const handleRecalculateMonthlyMetrics = async () => {
    if (!selectedBrand || !selectedMonth) {
      message.warning('Lütfen marka ve ay seçin');
      return;
    }

    try {
      setLoading(true);
      const result = await monthlyMetricService.recalculateMonthlyMetrics(
        selectedBrand,
        selectedMonth.format('YYYY-MM')
      );

      if (result.success) {
        message.success('Aylık metrikler başarıyla güncellendi');

        // Aylık metrikler backend'de günlük otomatik metrikleri de etkilediği için
        // tüm veriyi backend'den tekrar çekmemiz gerekiyor.
        await fetchMonthData();

        // MonthlyMetricsTable'ı yenile (Bu ayrı bir tablo, burası kalmalı)
        if (monthlyMetricsTableRef.current) {
          // fetchMonthlyValues zaten fetchMonthData sonrasında güncel veriyi almalı
          // ama emin olmak için tekrar çağırabiliriz veya fetchMonthData'nın bitmesini bekleyebiliriz.
          // Şimdilik sadece log bırakalım.
          console.log('MonthlyMetricsTable yenilenmesi fetchMonthData tarafından tetiklenmeli.');
          // await monthlyMetricsTableRef.current.fetchMonthlyValues();
        }

        // // Ana grid'deki SADECE aylık metrik özet satırını güncelle (BU BLOK KALDIRILDI - fetchMonthData hallediyor)
        // if (monthlyValues && monthlyValues.length > 0) {
        //   setGridData(prevGridData => {
        //     const newGridData = [...prevGridData];
        //     const monthlyRowIndex = newGridData.findIndex(row => row.isMonthlyRow);

        //     if (monthlyRowIndex !== -1) {
        //       const monthlyRow = { ...newGridData[monthlyRowIndex] };
        //       metrics.forEach(metric => {
        //         const linkedMonthlyMetricId = metric.BrandMetric?.linkedMonthlyMetricId;
                
        //         if (linkedMonthlyMetricId) {
        //           const monthlyMetric = monthlyValues.find(m => m.id === linkedMonthlyMetricId);
        //           if (monthlyMetric) {
        //             monthlyRow[`metric_${metric.id}`] = monthlyMetric.value;
        //             console.log(`Metrik ${metric.id} için aylık değer güncellendi:`, monthlyMetric.value);
        //           }
        //         }
        //       });
              
        //       // Güncellenmiş satırı grid'e ekle
        //       newGridData[monthlyRowIndex] = monthlyRow;
        //       setGridData(newGridData);
              
        //       // HotTable'ı yeniden render et
        //       setTimeout(() => {
        //         if (hotTableRef && hotTableRef.current && hotTableRef.current.hotInstance) {
        //           hotTableRef.current.hotInstance.render();
        //         }
        //       }, 100);
        //     }
        //   });
        // }
      } else {
        message.error('Aylık metrikler güncellenirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Aylık metrik güncelleme hatası:', error);
      message.error('Aylık metrikler güncellenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // saveBatchData fonksiyonu kullanılmıyor, ancak ileride kullanılabilir
  // const saveBatchData = async () => {
  //   if (pendingChanges.size === 0) return;
  //   
  //   // Yükleme göstergesini sadece 300ms'den uzun süren işlemler için göster
  //   let loadingTimeout = setTimeout(() => {
  //     setLoading(true);
  //   }, 300);
  //   
  //   try {
  //       // ... existing code ...
  //   } catch (error) {
  //       // ... existing code ...
  //   } finally {
  //       // ... existing code ...
  //   }
  // };

  // Aylık metrik güncellendiğinde çağrılacak callback
  const handleMonthlyMetricUpdate = useCallback(async () => {
    console.log('Aylık metrik güncellendi, günlük metrikleri yeniden yüklüyorum...');
    try {
      // Önce aylık metrikleri yeniden hesapla
      console.log('Aylık metrikleri yeniden hesaplıyorum...');
      const recalculateResult = await monthlyMetricService.recalculateMonthlyMetrics(
        selectedBrand,
        selectedMonth.format('YYYY-MM')
      );

      if (recalculateResult.success) {
        console.log('Aylık metrikler başarıyla yeniden hesaplandı');
        
        // Sonra günlük verileri yeniden yükle
        await fetchMonthData();
        console.log('Günlük metrikler başarıyla güncellendi');
        
        message.success('Aylık metrikler yeniden hesaplandı ve günlük metrikler güncellendi');
      } else {
        console.warn('Aylık metrik yeniden hesaplama başarısız, sadece günlük veriler yenileniyor');
        await fetchMonthData();
        message.warning('Günlük metrikler güncellendi, ancak aylık metrik hesaplamasında sorun oluştu');
      }
    } catch (error) {
      console.error('Aylık metrik güncelleme sürecinde hata:', error);
      try {
        // Hata durumunda en azından günlük verileri yenilemeye çalış
        await fetchMonthData();
        message.warning('Günlük metrikler güncellendi, ancak aylık metrik hesaplamasında hata oluştu');
      } catch (fetchError) {
        console.error('Günlük metrikler de güncellenemedi:', fetchError);
        message.error('Metrikler güncellenirken bir hata oluştu');
      }
    }
  }, [selectedBrand, selectedMonth, fetchMonthData]);

  // Paralel worker'lar ile otomatik metrikleri hesapla
  const calculateAutomaticMetrics = async () => {
    const WORKER_COUNT = 4; // CPU çekirdek sayısına göre ayarlanabilir
    const workers = [];
    const results = new Map();
    
    try {
      // Worker'ları oluştur
      for (let i = 0; i < WORKER_COUNT; i++) {
        const worker = new Worker(new URL('../../workers/metricCalculator.worker.js', import.meta.url));
        workers.push(worker);
      }

      // Tüm worker'lardan gelecek sonuçları bekle
      const workerPromises = workers.map((worker, index) => 
        new Promise((resolve, reject) => {
          worker.onmessage = (event) => {
            results.set(event.data.workerId, event.data.results);
            resolve();
          };

          worker.onerror = (error) => {
            console.error('Worker hatası:', error);
            reject(error);
          };

          // Worker'a veri gönder
          const autoMetrics = metrics
            .filter(m => m.isAutoCalculated)
            .map(m => ({
              formula: m.BrandMetric?.formula,
              Metric: {
                id: m.id,
                name: m.name
              }
            }));

            console.log('Worker\'a gönderilen otomatik metrikler:', autoMetrics);

            const dailyDataCache = {};
            gridData.forEach((row, rowIndex) => {
              const entries = {};
              metrics.forEach(metric => {
                const value = row[`metric_${metric.id}`];
                if (value !== undefined && value !== '') {
                  entries[metric.id] = {
                    name: metric.name,
                    value: value
                  };
                }
              });
              if (Object.keys(entries).length > 0) {
                dailyDataCache[rowIndex] = entries;
              }
            });

            console.log('Worker\'a gönderilen veri önbelleği:', dailyDataCache);

            worker.postMessage({
              autoMetrics,
              dailyDataCache,
              workerId: index,
              totalWorkers: WORKER_COUNT
            });
        })
      );

      // Tüm worker'ların işini bitirmesini bekle
      await Promise.all(workerPromises);

      // Sonuçları birleştir
      const combinedResults = Array.from(results.values()).flat();
      console.log('Worker\'lardan gelen sonuçlar:', combinedResults);

      // Her gün için bulk upsert verisi hazırla
      const bulkData = [];
      gridData.forEach(row => {
        combinedResults.forEach(result => {
          bulkData.push({
            brandId: selectedBrand,
            metricId: result.metricId,
            date: row.dateObj.format('YYYY-MM-DD'),
            value: result.value
          });
        });
      });

      console.log('Kaydedilecek bulk veri:', bulkData);

      // Tek seferde bulk upsert yap
      if (bulkData.length > 0) {
        await api.post('/data/bulk', {
          data: bulkData
        });
      }

      // Verileri yenile
      await fetchMonthData();

    } catch (error) {
      console.error('Otomatik metrik hesaplama hatası:', error);
      message.error('Otomatik metrikler hesaplanırken bir hata oluştu');
    } finally {
      // Worker'ları temizle
      workers.forEach(worker => worker.terminate());
    }
  };

  // Metrik görünürlüğünü değiştirme fonksiyonu
  const handleMetricVisibilityChange = useCallback((metricId, isVisible) => {
    setMetricVisibility(prev => ({
      ...prev,
      [metricId]: isVisible
    }));
  }, []);

  // Görünürlük değişikliklerini kaydetme
  const saveMetricVisibility = useCallback(async () => {
    if (!selectedBrand) return;

    try {
      setLoading(true);
      
      // Backend'e gönderilecek metrik listesini hazırla
      const metricUpdates = allMetrics.map(metric => ({
        metricId: metric.id,
        displayOrder: metric.BrandMetric?.displayOrder || 0,
        code: metric.BrandMetric?.code || null,
        variables: metric.BrandMetric?.variables || null,
        isVisible: metricVisibility[metric.id] !== false
      }));

      const response = await api.put(`/brands/${selectedBrand}/metrics`, {
        metrics: metricUpdates
      });

      if (response.data.success) {
        message.success('Metrik görünürlük ayarları kaydedildi');
        // Görünür metrikleri güncelle
        const visibleMetrics = allMetrics.filter(
          metric => metricVisibility[metric.id] === true
        );
        setMetrics(visibleMetrics);
        setVisibilityModalVisible(false);
        
        // Tablodaki verileri yeniden yükle
        fetchMonthData();
      } else {
        throw new Error('Metrik görünürlükleri kaydedilemedi');
      }
    } catch (error) {
      console.error('Metrik görünürlüğü güncelleme hatası:', error);
      message.error('Görünürlük ayarları kaydedilirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [allMetrics, metricVisibility, selectedBrand, fetchMonthData]);

  // Tüm metrikleri görünür/gizli yap
  const handleSetAllVisibility = useCallback((visible) => {
    const newVisibility = {};
    allMetrics.forEach(metric => {
      newVisibility[metric.id] = visible;
    });
    setMetricVisibility(newVisibility);
  }, [allMetrics]);

  // Metrik görünürlük modalı
  const renderVisibilityModal = () => (
    <Modal
      title="Metrik Görünürlük Ayarları"
      open={visibilityModalVisible}
      onOk={saveMetricVisibility}
      onCancel={() => setVisibilityModalVisible(false)}
      width={600}
      confirmLoading={loading}
      okText="Değişiklikleri Kaydet"
      cancelText="İptal"
    >
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button onClick={() => handleSetAllVisibility(true)}>Tümünü Göster</Button>
          <Button onClick={() => handleSetAllVisibility(false)}>Tümünü Gizle</Button>
        </Space>
      </div>
      <List
        bordered
        dataSource={allMetrics}
        renderItem={metric => (
          <List.Item
            key={metric.id}
            actions={[
              <span key="status" style={{ marginRight: 8 }}>
                {metricVisibility[metric.id] === true ? 
                  <Typography.Text type="success">Görünür</Typography.Text> : 
                  <Typography.Text type="secondary">Gizli</Typography.Text>
                }
              </span>,
              <Switch
                key="switch"
                checked={metricVisibility[metric.id] === true}
                onChange={(checked) => handleMetricVisibilityChange(metric.id, checked)}
                checkedChildren={<EyeOutlined />}
                unCheckedChildren={<EyeInvisibleOutlined />}
              />
            ]}
          >
            <List.Item.Meta
              title={metric.name}
              description={
                <Tooltip title={metric.internalNotes || (metric.isAutoCalculated ? 'Otomatik Hesaplanan' : 'Manuel')}>
                  <span style={{ color: metric.isAutoCalculated ? '#1890ff' : '#52c41a' }}>
                    {metric.isAutoCalculated ? 'Otomatik Hesaplanan' : 'Manuel'}
                  </span>
                </Tooltip>
              }
            />
          </List.Item>
        )}
      />
    </Modal>
  );

  useEffect(() => {
    fetchBrands();
  }, []);

  useEffect(() => {
    if (selectedBrand) {
      fetchBrandMetrics();
    }
  }, [selectedBrand, fetchBrandMetrics]);

  useEffect(() => {
    if (selectedBrand && metrics.length > 0) {
      fetchMonthData();
    }
  }, [selectedBrand, selectedMonth, metrics, fetchMonthData]);

  if (initialLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2}>Veri Girişi</Title>
        <Space>
          <Button
            icon={<SettingOutlined />}
            onClick={() => setVisibilityModalVisible(true)}
            disabled={!selectedBrand || allMetrics.length === 0}
          >
            Günlük Metrik Görünürlük
          </Button>
          <Button
            icon={<SettingOutlined />}
            onClick={() => monthlyMetricsTableRef.current?.setVisibilityModalVisible(true)}
            disabled={!selectedBrand || !monthlyMetricsTableRef.current?.gridData?.originalData?.length}
          >
            Aylık Metrik Görünürlük
          </Button>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={handleRecalculateMonthlyMetrics}
            loading={loading}
          >
            Aylık Metrikleri Güncelle
          </Button>
        </Space>
      </div>

      <Card bodyStyle={{ padding: '12px' }}>
        <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
          <Select
            value={selectedBrand}
            onChange={setSelectedBrand}
            placeholder="Marka seçin"
            style={{ width: 200 }}
            loading={loading}
            disabled={loading}
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {brands.map(brand => (
              <Select.Option key={brand.id} value={brand.id}>
                {brand.name}
              </Select.Option>
            ))}
          </Select>

          <DatePicker
            value={selectedMonth}
            onChange={handleMonthChange}
            picker="month"
            style={{ width: 200 }}
            format="MMMM YYYY"
            disabled={!selectedBrand || loading}
            allowClear={false}
            disabledDate={disabledDate}
            inputReadOnly={true}
            defaultValue={dayjs().startOf('month')}
            getPopupContainer={(trigger) => trigger.parentNode}
            open={isPickerOpen}
            onOpenChange={setIsPickerOpen}
            panelRender={(panel) => (
              <div>
                {React.cloneElement(panel, {
                  onSelect: (date) => {
                    handleMonthChange(date);
                  }
                })}
              </div>
            )}
          />
        </div>

        {selectedBrand && selectedMonth && (
          <div >
            <Title level={5} style={{ marginBottom: 8 }}>Aylık Metrikler</Title>
            <MonthlyMetricsTable
              ref={monthlyMetricsTableRef}
              brandId={selectedBrand}
              selectedMonth={selectedMonth}
              onMonthlyMetricUpdate={handleMonthlyMetricUpdate}
            />
          </div>
        )}

        <div style={{ height: 'calc(100vh - 250px)', overflow: 'hidden', position: 'relative' }}>
          {loading && (
            <div style={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              background: 'rgba(255, 255, 255, 0.7)', 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              zIndex: 1000 
            }}>
              <Spin size="large" />
            </div>
          )}
          <HotTable
            ref={hotTableRef}
            data={gridData}
            columns={columns}
            colHeaders={true}
            rowHeaders={function(index) {
              if (!gridData[index]) return index + 1;
              
              // Aylık satır için özel başlık
              if (gridData[index].isMonthlyRow) {
                return 'AYLIK';
              }
              
              // Diğer satırlar için tarih başlığı
              return gridData[index].dateObj ? gridData[index].dateObj.format('D MMM') : (index + 1);
            }}
            height="100%"
            licenseKey="non-commercial-and-evaluation"
            afterChange={handleGridChange}
            stretchH="all"
            contextMenu={true}
            enterBeginsEditing={true}
            autoWrapRow={true}
            autoWrapCol={true}
            className="custom-handsontable"
            cells={(row, col, prop) => {
              // Aylık metrik satırı için özel stil
              if (gridData[row] && gridData[row].isMonthlyRow) {
                return {
                  readOnly: true,
                  className: 'monthly-metric-row',
                  renderer: function(instance, td, row, col, prop, value, cellProperties) {
                    Handsontable.renderers.TextRenderer.apply(this, arguments);
                    td.style.backgroundColor = '#e6f7ff';
                    td.style.fontWeight = 'bold';
                    td.style.color = '#1890ff';
                  }
                };
              }
              
              // Metrik sütunları için renk ve özel renderer uygula
              if (col > 0 && columns[col]) {
                const metricId = columns[col].data.replace('metric_', '');
                const metric = metrics.find(m => m.id.toString() === metricId);
                
                if (metric) {
                  return {
                    renderer: function(instance, td, row, col, prop, value, cellProperties) {
                      // String tipindeki metrikler için özel renderer
                      if (metric.dataType === 'text' && value) {
                        // Önce varsayılan text renderer'ı uygula
                        Handsontable.renderers.TextRenderer.apply(this, arguments);
                        
                        // Eğer metin 42 karakterden uzunsa
                        if (value.length > 42) {
                          // Tooltip ekle
                          td.title = value;
                          // İçeriği kısalt
                          td.textContent = value.substring(0, 42) + '...';
                        }
                      } else {
                        // Diğer tipler için normal renderer
                        Handsontable.renderers.NumericRenderer.apply(this, arguments);
                        
                        // Sayısal değer kontrolü
                        if (value !== null && value !== undefined && value !== '') {
                          // Sayısal değeri formatla (her 3 hanede bir virgül ekle)
                          const numValue = parseFloat(value);
                          if (!isNaN(numValue)) {
                            // Ondalık kısmı ayır
                            const parts = td.textContent.split('.');
                            const integerPart = parts[0];
                            const decimalPart = parts.length > 1 ? '.' + parts[1] : '';
                            
                            // Eğer zaten formatlanmamışsa, her 3 hanede bir virgül ekle
                            if (!integerPart.includes(',')) {
                              // Sayıyı formatla
                              const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                              td.textContent = formattedInteger + decimalPart;
                            }
                          }
                          
                          // Yüzde tipi için % işareti ekle
                          if (metric.dataType === 'percentage') {
                            // Eğer değer zaten % içermiyorsa ekle
                            if (!td.textContent.includes('%')) {
                              td.textContent = td.textContent + '%';
                            }
                          }
                        }
                      }
                      
                      // Metriğin rengini başlık hücresine uygula
                      if (instance.getColHeader()[col] && metric.color) {
                        const headerElement = instance.rootElement.querySelector(`thead tr:first-child th:nth-child(${col + 2})`);
                        if (headerElement) {
                          headerElement.style.backgroundColor = metric.color;
                          const isLightColor = isColorLight(metric.color);
                          headerElement.style.color = isLightColor ? '#000000' : '#FFFFFF';
                          
                          // Başlık için tooltip ekle (metrik adını göster)
                          if (metric.description && metric.description !== metric.name) {
                            headerElement.title = `${metric.name} (${metric.description})`;
                          } else {
                            headerElement.title = metric.name;
                          }
                        }
                        
                        // Hücre için hafif bir arka plan rengi
                        const lightColor = lightenColor(metric.color, 0.85);
                        td.style.backgroundColor = lightColor;
                      }
                      
                      // Otomatik hesaplanan metrikler için farklı stil
                      if (metric.isAutoCalculated) {
                        td.style.fontStyle = 'italic';
                        td.style.color = '#666666';
                      }
                      
                      // API'den gelen veriler için hafif mor arka plan
                      const rowIndex = row;
                      const currentDate = gridData[rowIndex]?.dateObj?.format('YYYY-MM-DD');
                      
                      // API yanıtından ilgili veri noktasını bul
                      if (currentDate) {
                        const apiData = dataForGrid.find(item => 
                          (item.Metric?.id === metric.id || item.metricId === metric.id) && 
                          dayjs(item.date).format('YYYY-MM-DD') === currentDate
                        );
                        
                        // Eğer veri kaynağı 'api' ise hafif mor renk uygula
                        if (apiData && apiData.source === 'api') {
                          td.classList.add('api-data-cell');
                          td.title = td.title ? `${td.title} (API Verisi)` : 'API Verisi';
                        }
                      }
                    }
                  };
                }
              }
              
              return {};
            }}
          />
        </div>
      </Card>

      {renderVisibilityModal()}
    </div>
  );
};

export default DataEntry; 